import { ChangeEvent, Dispatch, FC, useEffect, useState } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { SelectChangeEvent } from '@mui/material/Select';
import dayjs, { Dayjs } from 'dayjs';
import { Value, isPossiblePhoneNumber } from 'react-phone-number-input';

import { CustomButton, CustomInput, CustomPhoneInput, CustomSelect, MultipleSelect, Picker, Text } from '../../../shared/uiComponents';
import { capitalizer, dateNormalizer, errorNormalizer, validateEmail, validateName } from '../../../shared/Helpers/functions';
import AddressInput from '../../../shared/uiComponents/Input/addressInput';
import { AdminContainer as Container, InputWrapper, Loader } from '../../../components/StyledComponents';
import { ACTIONS, ActionProperties } from './clientReducer';
import { InsuranceSelect } from '../../../components/InsuranceSelect';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { getDiagnosisCodes, getStatuses } from '../../../redux/State/clientSlice/clientSlice';
import {
	AddClientProperties,
	DuplicateMatchProperties,
	UpdateClientInfoProperties,
} from '../../../redux/API/ClientAPIHelpers/clientProperties';
import { getCaseCoordinators } from '../../../redux/State/clientSlice/userClientSlice';
import { InsuranceAuthorizationProperties } from '../../../redux/API/ClientAPIHelpers/insuranceAuthorizationProperties';
import { getInsurances } from '../../../redux/State/clientSlice/insuranceSlice';
import { getUserBranches } from '../../../redux/State/userSlice';
import { addClientFunder, getClientFunders, getFunders, removeClientFunder } from '../../../redux/State/clientSlice/fundingSlice';
import { DEFAULT_DIAGNOSIS_TEXT } from '../constants';
import { DEFAULT_TIMEZONE_ID, TIMEZONE_OPTIONS } from '../../../shared/Helpers/timezones';

const genderOptions = [
	{ id: 'male', name: 'Male' },
	{ id: 'female', name: 'Female' },
	{ id: 'other', name: 'Other' },
];

interface ClientFormProperties {
	clientInfo: AddClientProperties | UpdateClientInfoProperties;
	duplicateWarnings?: DuplicateMatchProperties[];
	saveClientHandler: () => void;
	serverErrors?: Record<string, string>;
	setClientInfo: Dispatch<ActionProperties>;
	onFunderSelectionChange?: (funderIds: string[]) => void;
}

interface ErrorProperties {
	branch: string;
	dateOfBirthday: string;
	email: string;
	firstName: string;
	gender: string;
	insurance: string;
	lastName: string;
	mbhId: string;
	parentPhoneNumber: string;
}

interface ValidationErrorProperties {
	nickName: string;
	secondaryEmail: string;
	secondaryNumber: string;
}

const ClientForm: FC<ClientFormProperties> = ({
	clientInfo,
	setClientInfo,
	saveClientHandler,
	duplicateWarnings,
	serverErrors,
	onFunderSelectionChange,
}) => {
	const dispatch = useDispatch<DispatchProperties>();
	const { clientId } = useParams();

	const [dateValue, setDateValue] = useState<Dayjs | null>(null);
	const [error, setError] = useState<ErrorProperties>({
		firstName: '',
		lastName: '',
		email: '',
		parentPhoneNumber: '',
		dateOfBirthday: '',
		gender: '',
		insurance: '',
		branch: '',
		mbhId: '',
	});

	const [validationError, setValidationError] = useState<ValidationErrorProperties>({
		nickName: '',
		secondaryEmail: '',
		secondaryNumber: '',
	});

	const [duplicateFieldWarnings, setDuplicateFieldWarnings] = useState<Partial<ErrorProperties>>({});

	const [disableSave, setDisableSave] = useState<boolean>(true);

	const loading = useSelector((state) => state.client.loading);
	const fundingLoading = useSelector((state) => state.funding.loading);
	const insurances = useSelector((state) => state.insurance.insurances);
	const statuses = useSelector((state) => state.client.statuses);
	const diagnosisCodes = useSelector((state) => state.client.diagnosisCodes);
	const caseCoordinators = useSelector((state) => state.userClient.caseCoordinators);
	const branches = useSelector((state) => state.userBranch.userBranches);
	const userId = useSelector((state) => state.account.user.id);
	const funders = useSelector((state) => state.funding.funders);
	const clientFunders = useSelector((state) => state.funding.clientFunders);

	// For new clients, track funder selections locally (not yet persisted).
	const [newClientFunderIds, setNewClientFunderIds] = useState<string[]>([]);

	useEffect(() => {
		dispatch(getInsurances({ includeOutOfNetwork: true }));
		dispatch(getStatuses());
		dispatch(getDiagnosisCodes());
		dispatch(getCaseCoordinators());
		dispatch(getFunders());
	}, [dispatch]);

	useEffect(() => {
		if (!clientId) return;
		dispatch(getClientFunders(clientId));
	}, [dispatch, clientId]);

	const funderInitialValue = clientId
		? clientFunders.map((cf) => cf.funderId)
		: newClientFunderIds;

	const onFunderChange = (event: SelectChangeEvent<string[]>) => {
		const raw = event.target.value;
		const selected = typeof raw === 'string' ? raw.split(',').filter(Boolean) : raw;

		if (clientId) {
			const currentIds = new Set(clientFunders.map((cf) => cf.funderId));
			const selectedIds = new Set(selected);

			selected.forEach((id) => {
				if (!currentIds.has(id)) {
					dispatch(addClientFunder({ clientId, funderId: id }))
						.then(unwrapResult)
						.catch(errorNormalizer);
				}
			});

			clientFunders.forEach((cf) => {
				if (!selectedIds.has(cf.funderId)) {
					dispatch(removeClientFunder({ clientId, clientFunderId: cf.id }))
						.then(unwrapResult)
						.catch(errorNormalizer);
				}
			});
		} else {
			setNewClientFunderIds(selected);
			onFunderSelectionChange?.(selected);
		}
	};

	useEffect(() => {
		if (!userId) return;
		dispatch(getUserBranches(userId));
	}, [dispatch, userId]);

	useEffect(() => {
		if (Boolean(clientInfo.email) && !validateEmail(clientInfo.email)) {
			setError((prev) => ({ ...prev, email: 'Invalid email' }));
		} else {
			setError((prev) => ({ ...prev, email: '' }));
		}

		if (Boolean(clientInfo.secondaryEmail) && !validateEmail(clientInfo.secondaryEmail)) {
			setValidationError((prev) => ({
				...prev,
				secondaryEmail: 'Invalid email',
			}));
		} else {
			setValidationError((prev) => ({ ...prev, secondaryEmail: '' }));
		}
	}, [clientInfo]);

	useEffect(() => {
		if (!diagnosisCodes) return;
		const defaultCode = diagnosisCodes.find((x) => x.isDefault);
		if (!defaultCode) return;
		setClientInfo({
			type: ACTIONS.setDiagnosisCodeId,
			payload: defaultCode.id,
		});
	}, [diagnosisCodes, setClientInfo]);

	useEffect(() => {
		if (!clientInfo.dateOfBirthday) return;
		setDateValue(dayjs(clientInfo.dateOfBirthday));
	}, [clientInfo.dateOfBirthday]);

	useEffect(() => {
		if (!clientInfo.dateOfBirthday) return;
		setError((prev) => ({ ...prev, dateOfBirthday: '' }));
	}, [clientInfo.dateOfBirthday]);

	useEffect(() => {
		const { firstName, lastName, dateOfBirthday, gender, email, parentPhoneNumber } = clientInfo;

		const values = [firstName, lastName, dateOfBirthday, gender, email, parentPhoneNumber];
		setDisableSave(Object.values(error).some((x) => Boolean(x)) || values.some((x) => !x));
	}, [error, clientInfo]);

	useEffect(() => {
		if (!duplicateWarnings?.length) {
			setDuplicateFieldWarnings({});

			return;
		}
		const warnings: Partial<ErrorProperties> = {};
		duplicateWarnings.forEach((warning) => {
			if (warning.matchField === 'email') warnings.email = 'This email already exists';
			if (warning.matchField === 'phone') warnings.parentPhoneNumber = 'This phone number already exists';

			if (warning.matchField === 'name') {
				warnings.firstName = 'A client with this name already exists';
				warnings.lastName = 'A client with this name already exists';
			}
		});
		setDuplicateFieldWarnings(warnings);
	}, [duplicateWarnings]);

	useEffect(() => {
		if (!serverErrors) return;
		const fieldErrors: Partial<ErrorProperties> = {};
		if (serverErrors.mbhId) fieldErrors.mbhId = serverErrors.mbhId;
		setError((prev) => ({ ...prev, ...fieldErrors }));
	}, [serverErrors]);

	const onFirstNameChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setClientInfo({
			type: ACTIONS.setFirstName,
			payload: capitalizer(value),
		});
		setError((prev) => ({ ...prev, firstName: '' }));
		setDuplicateFieldWarnings((prev) => ({ ...prev, firstName: '' }));
	};

	const onLastNameChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setClientInfo({
			type: ACTIONS.setLastName,
			payload: capitalizer(value),
		});
		setError((prev) => ({ ...prev, lastName: '' }));
		setDuplicateFieldWarnings((prev) => ({ ...prev, lastName: '' }));
	};

	const onNickNameChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setClientInfo({
			type: ACTIONS.setNickName,
			payload: capitalizer(value),
		});
		setValidationError((prev) => ({ ...prev, nickName: '' }));
	};

	const onEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setClientInfo({ type: ACTIONS.setEmail, payload: value });
		setError((prev) => ({ ...prev, email: '' }));
		setDuplicateFieldWarnings((prev) => ({ ...prev, email: '' }));
	};

	const onSecondaryEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setClientInfo({ type: ACTIONS.setSecondaryEmail, payload: value });
		setValidationError((prev) => ({ ...prev, secondaryEmail: '' }));
	};

	const onPhoneNumberChange = (value: Value) => {
		setClientInfo({ type: ACTIONS.setParentPhoneNumber, payload: value });
		setError((prev) => ({ ...prev, parentPhoneNumber: '' }));
		setDuplicateFieldWarnings((prev) => ({ ...prev, parentPhoneNumber: '' }));
	};

	const onSecondaryPhoneNumberChange = (value: Value) => {
		setClientInfo({ type: ACTIONS.setSecondaryPhoneNumber, payload: value });
		setValidationError((prev) => ({ ...prev, secondaryNumber: '' }));
	};

	const onDateOfBirthChange = (value: Dayjs | null) => {
		if (!value) return;
		setDateValue(value);
		if (!dayjs(value).isValid()) return;
		const payload = dateNormalizer(value);
		setClientInfo({ type: ACTIONS.setDateOfBirth, payload });
		setError((prev) => ({ ...prev, dateOfBirthday: '' }));
	};

	const onGenderChange = (event: SelectChangeEvent<string>) => {
		const value = event.target.value;
		setClientInfo({ type: ACTIONS.setGender, payload: value });
		setError((prev) => ({ ...prev, gender: '' }));
	};

	const onAddressChange = ({
		fullAddress,
		city,
		zipcode,
	}: {
		city?: string;
		fullAddress?: string;
		zipcode?: string;
	}) => {
		setClientInfo({ type: ACTIONS.setAddress, payload: fullAddress });
		setClientInfo({ type: ACTIONS.setCity, payload: city });
		setClientInfo({ type: ACTIONS.setZip, payload: zipcode });
		setError((prev) => ({ ...prev, address: '' }));
	};

	const onStatusChange = (event: SelectChangeEvent<string>) => {
		const value = parseInt(event.target.value);
		setClientInfo({ type: ACTIONS.setStatus, payload: value });
	};

	const onTimeZoneChange = (event: SelectChangeEvent<string>) => {
		setClientInfo({ type: ACTIONS.setTimeZoneId, payload: event.target.value });
	};

	const onPrimaryInsuranceChange = (value: string | null) => {
		if (!value) return;
		const payload = clientInfo.insurances?.length
			? clientInfo.insurances.map((insurance) => {
					if (insurance.type === 2) return insurance;

					return {
						...insurance,
						insuranceId: value,
					};
				})
			: [
					{
						insuranceId: value,
						type: 1,
						insuranceNumber: '',
					} as InsuranceAuthorizationProperties,
				];
		setClientInfo({ type: ACTIONS.setInsurances, payload });
		setError((prev) => ({ ...prev, insurance: '' }));
	};

	const onPrimaryInsuranceNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		const payload = clientInfo.insurances.map((insurance) => {
			if (insurance.type === 2) return insurance;

			return {
				...insurance,
				insuranceNumber: value,
			};
		});
		setClientInfo({ type: ACTIONS.setInsurances, payload });
	};

	const onSecondaryInsuranceChange = (value: string | null) => {
		const clientInsurances = clientInfo.insurances;

		if (!value) {
			setClientInfo({
				type: ACTIONS.setInsurances,
				payload: clientInsurances.filter((x) => x.type === 1),
			});

			return;
		}
		if (!clientInsurances.length) return;

		const payload =
			clientInsurances.length > 1
				? clientInsurances.map((insurance) => {
						if (insurance.type === 1) return insurance;

						return {
							...insurance,
							insuranceId: value,
						};
					})
				: [
						...clientInsurances,
						{
							insuranceId: value,
							type: 2,
							insuranceNumber: '',
						} as InsuranceAuthorizationProperties,
					];
		setClientInfo({ type: ACTIONS.setInsurances, payload });
	};

	const onSecondaryInsuranceNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		const payload = clientInfo.insurances.map((insurance) => {
			if (insurance.type === 1) return insurance;

			return {
				...insurance,
				insuranceNumber: value,
			};
		});
		setClientInfo({ type: ACTIONS.setInsurances, payload });
	};

	const onCaseCoordinatorChange = (event: SelectChangeEvent<string>) => {
		const value = event.target.value;
		if (!value) return;
		setClientInfo({ type: ACTIONS.setCaseCoordinatorId, payload: value });
	};

	const onMBHIdChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setClientInfo({ type: ACTIONS.setMBHId, payload: value });
		setError((prev) => ({ ...prev, mbhId: '' }));
	};

	const handleAdditionalInfoChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setClientInfo({ type: ACTIONS.setOtherDiagnosisCodes, payload: value });
	};

	const onFirstNameBlurHandler = () => {
		if (Boolean(clientInfo.firstName) && validateName(clientInfo.firstName)) return;

		if (!clientInfo.firstName) {
			setError((prev) => ({ ...prev, firstName: 'Required' }));

			return;
		}
		setError((prev) => ({ ...prev, firstName: 'Invalid name' }));
	};

	const onLastNameBlurHandler = () => {
		if (Boolean(clientInfo.lastName) && validateName(clientInfo.lastName)) return;

		if (!clientInfo.lastName) {
			setError((prev) => ({ ...prev, lastName: 'Required' }));

			return;
		}
		setError((prev) => ({ ...prev, lastName: 'Invalid name' }));
	};

	const onNickNameBlurHandler = () => {
		if (validateName(clientInfo.firstName)) return;
		setValidationError((prev) => ({ ...prev, nickName: 'Invalid name' }));
	};

	const onEmailBlurHandler = () => {
		if (clientInfo.email) return;
		setError((prev) => ({ ...prev, email: 'Required' }));
	};

	const onPhoneNumberBlurHandler = () => {
		const { parentPhoneNumber } = clientInfo;

		if (!parentPhoneNumber) {
			setError((prev) => ({ ...prev, parentPhoneNumber: 'Required' }));

			return;
		}

		if (!isPossiblePhoneNumber(parentPhoneNumber)) {
			setError((prev) => ({
				...prev,
				parentPhoneNumber: 'Invalid phone number',
			}));

			return;
		}
	};

	const onSecondaryNumberBlurHandler = () => {
		const { secondaryNumber } = clientInfo;
		if (!secondaryNumber) return;
		if (!isPossiblePhoneNumber(secondaryNumber))
			setValidationError((prev) => ({
				...prev,
				secondaryNumber: 'Invalid phone number',
			}));
	};

	const onDateOfBirthdayBlurHandler = () => {
		if (clientInfo.dateOfBirthday) return;
		setError((prev) => ({ ...prev, dateOfBirthday: 'Required' }));
	};

	const onGenderBlurHandler = () => {
		if (clientInfo.gender) return;
		setError((prev) => ({ ...prev, gender: 'Required' }));
	};

	const onInsuranceBlurHandler = () => {
		if (clientInfo.insurances?.find((x) => x.type === 1)) return;
		setError((prev) => ({ ...prev, insurance: 'Required' }));
	};

	const onBranchChange = (event: SelectChangeEvent<string>) => {
		const payload = event.target.value;
		setClientInfo({ type: ACTIONS.setBranchId, payload });
		setError((prev) => ({ ...prev, branch: '' }));
	};

	const onBranchBlurHandler = () => {
		if (clientInfo.branchId) return;
		setError((prev) => ({ ...prev, branch: 'Required' }));
	};

	const primaryInsurance = clientInfo.insurances?.find((x) => x.type === 1);
	const primaryInsuranceId = primaryInsurance ? primaryInsurance.insuranceId : '';
	const primaryInsuranceNumber = primaryInsurance ? primaryInsurance.insuranceNumber : '';

	const secondaryInsurance = clientInfo.insurances?.find((x) => x.type === 2);
	const secondaryInsuranceId = secondaryInsurance ? secondaryInsurance.insuranceId : '';
	const secondaryInsuranceNumber = secondaryInsurance ? secondaryInsurance.insuranceNumber : '';

	return loading ? (
		<Loader />
	) : (
		<Container
			sx={{
				maxWidth: '100%',
				'@media screen and (max-width:768px)': {
					padding: '24px',
				},
			}}
		>
			<Text title="Client Information" type="h4" font="header" fontWeight="semiBold" className="marginBottom16" />
			<InputWrapper>
				<CustomInput
					label="First Name"
					value={clientInfo.firstName}
					onChange={onFirstNameChange}
					onBlur={onFirstNameBlurHandler}
					error={Boolean(error.firstName) || Boolean(duplicateFieldWarnings.firstName)}
					errorMessage={error.firstName || duplicateFieldWarnings.firstName}
				/>
				<CustomInput
					label="Last Name"
					value={clientInfo.lastName}
					onChange={onLastNameChange}
					onBlur={onLastNameBlurHandler}
					error={Boolean(error.lastName) || Boolean(duplicateFieldWarnings.lastName)}
					errorMessage={error.lastName || duplicateFieldWarnings.lastName}
				/>
			</InputWrapper>
			<InputWrapper>
				<CustomInput
					label="Nickname"
					value={clientInfo.nickName ?? ''}
					onChange={onNickNameChange}
					onBlur={onNickNameBlurHandler}
					error={Boolean(validationError.nickName)}
					errorMessage={validationError.nickName}
				/>
				<CustomSelect
					data={genderOptions}
					label="Gender"
					value={clientInfo.gender}
					setValue={onGenderChange}
					onBlur={onGenderBlurHandler}
					error={Boolean(error.gender)}
					errorMessage={error.gender}
				/>
			</InputWrapper>

			<InputWrapper>
				<CustomInput
					label="Email"
					value={clientInfo.email}
					onChange={onEmailChange}
					onBlur={onEmailBlurHandler}
					error={Boolean(error.email) || Boolean(duplicateFieldWarnings.email)}
					errorMessage={error.email || duplicateFieldWarnings.email}
				/>
				<CustomInput
					label="Secondary Email"
					value={clientInfo.secondaryEmail}
					onChange={onSecondaryEmailChange}
					error={Boolean(validationError.secondaryEmail)}
					errorMessage={validationError.secondaryEmail}
				/>
			</InputWrapper>
			<InputWrapper>
				<CustomPhoneInput
					label="Parent's Phone"
					value={clientInfo.parentPhoneNumber}
					onChange={onPhoneNumberChange}
					onBlur={onPhoneNumberBlurHandler}
					error={Boolean(error.parentPhoneNumber) || Boolean(duplicateFieldWarnings.parentPhoneNumber)}
					errorMessage={error.parentPhoneNumber || duplicateFieldWarnings.parentPhoneNumber}
				/>
				<CustomPhoneInput
					label="Secondary Phone"
					value={clientInfo.secondaryNumber}
					onChange={onSecondaryPhoneNumberChange}
					onBlur={onSecondaryNumberBlurHandler}
					error={Boolean(validationError.secondaryNumber)}
					errorMessage={validationError.secondaryNumber}
				/>
			</InputWrapper>
			<InputWrapper>
				<Picker.CustomDate
					label="Date of birth"
					value={dateValue}
					onChange={onDateOfBirthChange}
					onBlur={onDateOfBirthdayBlurHandler}
					disableFuture
					error={Boolean(error.dateOfBirthday)}
					errorMessage={error.dateOfBirthday}
				/>
				<CustomSelect
					label="Case Manager"
					data={caseCoordinators ? caseCoordinators.map((x) => ({ id: x.id, name: x.fullName })) : []}
					value={clientInfo.caseCoordinatorId}
					setValue={onCaseCoordinatorChange}
				/>
			</InputWrapper>
			<AddressInput setAddressDetails={onAddressChange} value={clientInfo.address} />
			<InputWrapper>
				<CustomSelect
					label="Client Timezone"
					data={TIMEZONE_OPTIONS}
					value={clientInfo.timeZoneId || DEFAULT_TIMEZONE_ID}
					setValue={onTimeZoneChange}
					className="marginBottom16"
				/>
			</InputWrapper>
			<InputWrapper>
				<InsuranceSelect
					label="Primary Insurance"
					data={insurances}
					value={primaryInsuranceId}
					setValue={onPrimaryInsuranceChange}
					onBlur={onInsuranceBlurHandler}
					error={Boolean(error.insurance)}
					errorMessage={error.insurance}
				/>
				<CustomInput
					label="Primary Member ID"
					value={primaryInsuranceNumber}
					onChange={onPrimaryInsuranceNumberChange}
					className="marginBottom16"
					disabled={!primaryInsuranceId}
				/>
			</InputWrapper>
			<InputWrapper>
				<InsuranceSelect
					label="Secondary Insurance"
					data={[
						{
							id: '',
							name: 'Clear Insurance',
							subInsurances: [],
							branches: [],
							canBeAssigned: true,
						},
						...insurances,
					]}
					value={secondaryInsuranceId}
					setValue={onSecondaryInsuranceChange}
					disabled={!primaryInsuranceId}
					isClearable
				/>
				<CustomInput
					label="Secondary Member ID"
					value={secondaryInsuranceNumber}
					onChange={onSecondaryInsuranceNumberChange}
					className="marginBottom16"
					disabled={!secondaryInsuranceId}
				/>
			</InputWrapper>
			<InputWrapper>
				<MultipleSelect
					label="Funding Sources"
					data={funders}
					initialValue={funderInitialValue}
					setValue={onFunderChange}
					disabled={fundingLoading}
					className="marginBottom16"
				/>
			</InputWrapper>
			<InputWrapper>
				<CustomSelect
					label="Branch"
					data={branches}
					value={clientInfo.branchId}
					setValue={onBranchChange}
					error={Boolean(error.branch)}
					errorMessage={error.branch}
					className="marginBottom16"
					onBlur={onBranchBlurHandler}
				/>
				<CustomInput
					label="MBH ID"
					value={clientInfo.mbhId}
					onChange={onMBHIdChange}
					className="marginBottom16"
					error={Boolean(error.mbhId)}
					errorMessage={error.mbhId}
				/>
			</InputWrapper>
			{Boolean(clientId) && (
				<CustomSelect
					label="Status"
					className="marginBottom16"
					data={statuses}
					value={clientInfo.status.toString()}
					setValue={onStatusChange}
				/>
			)}
			<div>
				<Text title={DEFAULT_DIAGNOSIS_TEXT} type="pmd" fontWeight="semiBold" className="marginBottom16" />
			</div>
			<div>
				<CustomInput
					multiline
					label="Additional diagnosis info"
					value={clientInfo.otherDiagnosisCodes}
					className="marginBottom16"
					onChange={handleAdditionalInfoChange}
				/>
			</div>
			{/* {!!clientId && isAdmin && <SelectBranch />} */}
			<CustomButton title="Save" onClick={saveClientHandler} disabled={disableSave} className="marginBottom16" />
		</Container>
	);
};

export default ClientForm;
